/**
 * Portfolio Financials Redux slice — keyed by portfolioId so multiple
 * portfolios can be cached independently without overwriting each other.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as portfolioFinancialsService from 'services/portfolioFinancialsService'
import type { PortfolioFinancialsResponse } from 'types/portfolioFinancials'

const SLICE_NAME = 'portfolioFinancials'

type PortfolioFinancialsState = {
	entitiesByPortfolioId: Record<string, PortfolioFinancialsResponse>
	loadingByPortfolioId: Record<string, boolean>
	errorByPortfolioId: Record<string, string | null>
}

const initialState: PortfolioFinancialsState = {
	entitiesByPortfolioId: {},
	loadingByPortfolioId: {},
	errorByPortfolioId: {},
}

type RejectedPayload = { portfolioId: string; error: string }

export const fetchPortfolioFinancials = createAsyncThunk<
	{ portfolioId: string; data: PortfolioFinancialsResponse },
	string,
	{ rejectValue: RejectedPayload }
>(`${SLICE_NAME}/fetch`, async (portfolioId, { getState, rejectWithValue }) => {
	try {
		const state = getState() as any
		const token = state?.userLogin?.userInfo?.token
		const data = await portfolioFinancialsService.getPortfolioFinancials(portfolioId, token)
		return { portfolioId, data }
	} catch (err: any) {
		return rejectWithValue({
			portfolioId,
			error:
				err?.response?.data?.message ||
				err?.message ||
				'Failed to fetch portfolio financials',
		})
	}
})

const portfolioFinancialsSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		clearPortfolioFinancials(state, action: { payload: string }) {
			delete state.entitiesByPortfolioId[action.payload]
			delete state.loadingByPortfolioId[action.payload]
			delete state.errorByPortfolioId[action.payload]
		},
	},
	extraReducers: builder => {
		builder
			.addCase(fetchPortfolioFinancials.pending, (state, action) => {
				const portfolioId = action.meta.arg
				state.loadingByPortfolioId[portfolioId] = true
				state.errorByPortfolioId[portfolioId] = null
			})
			.addCase(fetchPortfolioFinancials.fulfilled, (state, action) => {
				const { portfolioId, data } = action.payload
				state.entitiesByPortfolioId[portfolioId] = data
				state.loadingByPortfolioId[portfolioId] = false
				state.errorByPortfolioId[portfolioId] = null
			})
			.addCase(fetchPortfolioFinancials.rejected, (state, action) => {
				const portfolioId = action.payload?.portfolioId ?? action.meta.arg
				state.loadingByPortfolioId[portfolioId] = false
				state.errorByPortfolioId[portfolioId] =
					action.payload?.error || action.error?.message || 'Failed to fetch portfolio financials'
			})
	},
})

export const { clearPortfolioFinancials } = portfolioFinancialsSlice.actions

// ────── Selectors ──────────────────────────────────────────────────────────

export const selectPortfolioFinancials = (
	state: any,
	portfolioId: string,
): PortfolioFinancialsResponse | undefined =>
	state.portfolioFinancials?.entitiesByPortfolioId?.[portfolioId]

export const selectPortfolioFinancialsLoading = (state: any, portfolioId: string): boolean =>
	!!state.portfolioFinancials?.loadingByPortfolioId?.[portfolioId]

export const selectPortfolioFinancialsError = (
	state: any,
	portfolioId: string,
): string | null => state.portfolioFinancials?.errorByPortfolioId?.[portfolioId] ?? null

export default portfolioFinancialsSlice
